// Shared rules of Beauty appointment lines, variants and add-ons (organizer and client flows).
//
// An appointment is a list of lines. A service line may carry a variant (`variantId`, `variantName`); an
// add-on gets its own line right after its service, with the SAME `serviceId` as its parent and an `addOnId`.
// So a list keyed only by `serviceId` would collide: use `claveDeLinea`.

export const esAdicional = (linea) => Boolean(linea?.addOnId);

/** `addOnId ?? serviceId`, the key that is unique per line. */
export const claveDeLinea = (linea, indice = 0) =>
  String(linea?.addOnId || linea?.serviceId || `${linea?.name || "linea"}-${indice}`);

/** The one display rule: `name (variantName)` for a variant, `+ name` for an add-on, `name` otherwise. */
export const nombreDeLinea = (linea) => {
  const nombre = linea?.name || "";
  if (esAdicional(linea)) return `+ ${nombre}`;
  return linea?.variantName ? `${nombre} (${linea.variantName})` : nombre;
};

/** "Corte (Largo) + Peinado, Color": services joined by commas, each add-on glued to its service with " + ". */
export const resumenDeLineas = (lineas) =>
  (lineas || []).reduce((texto, linea) => {
    if (!linea?.name) return texto;
    if (esAdicional(linea)) return texto ? `${texto} + ${linea.name}` : nombreDeLinea(linea);
    return texto ? `${texto}, ${nombreDeLinea(linea)}` : nombreDeLinea(linea);
  }, "");

export const tieneVariantes = (servicio) => Array.isArray(servicio?.variants) && servicio.variants.length > 0;
export const tieneAdicionales = (servicio) => Array.isArray(servicio?.addOns) && servicio.addOns.length > 0;
export const tieneOpciones = (servicio) => tieneVariantes(servicio) || tieneAdicionales(servicio);

const idDeOpcion = (o) => String(o?._id ?? o?.id ?? "");
export const idDeVariante = idDeOpcion;
export const idDeAdicional = idDeOpcion;

export const buscarVariante = (servicio, variantId) =>
  (servicio?.variants || []).find((v) => idDeOpcion(v) === String(variantId)) || null;

/**
 * A selection is `[{ serviceId, variantId, addOnIds }]` (variantId "" until chosen). `items` is what the back
 * receives: variantId only when there is one, addOnIds only when not empty.
 */
export const itemsDeSeleccion = (seleccion) =>
  (seleccion || []).map((s) => ({
    serviceId: s.serviceId,
    ...(s.variantId ? { variantId: s.variantId } : {}),
    ...(s.addOnIds?.length ? { addOnIds: s.addOnIds } : {}),
  }));

/** Query value for GET routes: the back reads ONE `items` param with the JSON (axios encodes it on the wire). */
export const itemsComoParametro = (seleccion) => JSON.stringify(itemsDeSeleccion(seleccion));

/** Every service of the selection that has variants already has one chosen. */
export const seleccionCompleta = (seleccion, servicios) =>
  (seleccion || []).every((s) => {
    const servicio = (servicios || []).find((x) => String(x._id ?? x.id) === String(s.serviceId));
    return !tieneVariantes(servicio) || Boolean(buscarVariante(servicio, s.variantId));
  });

/** Price and duration of one choice from the catalog (the back's availability response is the final word). */
export const totalDeEleccion = (servicio, variantId, addOnIds = []) => {
  const variante = buscarVariante(servicio, variantId);
  const base = variante || (tieneVariantes(servicio) ? null : servicio);
  const adicionales = (servicio?.addOns || []).filter((a) => addOnIds.includes(idDeOpcion(a)));
  return {
    minutos: (Number(base?.durationMinutes) || 0) + adicionales.reduce((t, a) => t + (Number(a.durationMinutes) || 0), 0),
    precio: (Number(base?.price) || 0) + adicionales.reduce((t, a) => t + (Number(a.price) || 0), 0),
  };
};

const extremos = (valores) => {
  const lista = valores.map(Number).filter((n) => Number.isFinite(n));
  return lista.length ? { min: Math.min(...lista), max: Math.max(...lista) } : null;
};

/** Cheapest price of a service with variants (`priceFrom` of the public detail when it comes). */
export const precioDesde = (servicio) => {
  if (servicio?.priceFrom != null) return Number(servicio.priceFrom);
  const e = extremos((servicio?.variants || []).map((v) => v.price));
  return e ? e.min : Number(servicio?.price) || 0;
};

/** `{ min, max }` minutes of a service with variants. */
export const rangoDeDuracion = (servicio) => {
  if (servicio?.durationFrom != null && servicio?.durationTo != null) {
    return { min: Number(servicio.durationFrom), max: Number(servicio.durationTo) };
  }
  const e = extremos((servicio?.variants || []).map((v) => v.durationMinutes));
  return e || { min: Number(servicio?.durationMinutes) || 0, max: Number(servicio?.durationMinutes) || 0 };
};

const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;
export const textoVariantes = (n) => plural(n, "variante", "variantes");
export const textoAdicionales = (n) => plural(n, "adicional", "adicionales");

/** Back codes of a wrong selection (`codigo` in the public and organizer APIs). */
const MENSAJES_SELECCION = {
  variant_required: "Elegí una variante en cada servicio que las tiene.",
  invalid_variant: "Una de las variantes elegidas ya no está disponible. Elegila de nuevo.",
  invalid_add_on: "Uno de los adicionales elegidos ya no está disponible. Elegilo de nuevo.",
  invalid_items: "No pudimos leer los servicios elegidos. Volvé a elegirlos.",
  duplicate_service: "Un mismo servicio no puede repetirse en el turno.",
};

/** Spanish message of a selection error, or "" when the error is something else. */
export const mensajeDeSeleccion = (error) => MENSAJES_SELECCION[error?.response?.data?.codigo] || "";

/**
 * Compact label of the surfaces that always joined services with " + " ("Corte + Color"): the old text for plain
 * appointments, the display rule (`resumenDeLineas`) as soon as a line has a variant or is an add-on.
 */
export const resumenCompacto = (lineas) => {
  const lista = (lineas || []).map((l) => (typeof l === "string" ? { name: l } : l)).filter((l) => l?.name);
  if (lista.some((l) => l.variantName || esAdicional(l))) return resumenDeLineas(lista);
  return lista.map((l) => l.name).join(" + ");
};
